<template>
  <Dialog
    v-model:open="isOpen"
    size="sm"
    :hide-close-button="!showDismissButton"
    data-test-id="role-events-dialog"
    :title="title"
  >
    <div class="flex flex-col items-center gap-2 text-center">
      <IconWrapper :color="details.colour" size="lg" class="mb-2 rounded-full">
        <component :is="details.icon" class="h-8 w-8" />
      </IconWrapper>

      <template v-if="isOrgRemoval">
        <h3 class="type-h5 text-gray-900">
          {{ t('users.roleEvents.orgRemoved.heading', { organisation: roleMessage.EntityName }) }}
        </h3>
        <p class="type-body-1 text-gray-600">
          {{
            showOrganisationPicker
              ? t('users.roleEvents.orgRemoved.description')
              : t('users.roleEvents.orgRemoved.noOtherOrgsDescription')
          }}
        </p>
      </template>
      <template v-else>
        <h3 v-if="showEntityName" class="type-h5 text-gray-900">
          {{ roleMessage.EntityName }}
        </h3>
        <Tag
          :color="details.colour"
          :label="getRoleDisplayName(roleMessage.RoleCode)"
          :is-truncated="false"
        />
        <p class="type-body-1 text-gray-600">{{ details.description }}</p>
      </template>
    </div>

    <div v-if="showOrganisationPicker" class="mt-6">
      <p class="type-body-2 mb-2 text-gray-500">
        {{ t('users.roleEvents.orgRemoved.continueIn') }}
      </p>
      <ul class="grid max-h-80 gap-2 overflow-y-auto">
        <li v-for="org in organisations" :key="org.id">
          <button
            type="button"
            class="flex w-full items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 text-left hover:bg-gray-50 focus-visible:bg-gray-50"
            :data-cy="`org-select-modal-org-${org.id}`"
            @click="setNewOrg(org)"
          >
            <span aria-hidden="true" class="shrink-0">
              <Avatar :label="org.name" :src="org.logoUrl || undefined" size="sm" />
            </span>
            <span class="type-body-1-strong min-w-0 flex-1 wrap-break-word text-gray-900">
              {{ org.name }}
            </span>
            <ChevronRightIcon class="h-5 w-5 shrink-0 text-gray-400" />
          </button>
        </li>
      </ul>
    </div>

    <div v-if="showSpinner" class="mt-6 flex justify-center">
      <Spinner />
    </div>

    <p class="type-body-2 mt-6 text-center text-gray-500">
      {{ t('users.roleEvents.additionalMessage') }}
    </p>

    <template v-if="showOrganisationPicker || showDismissButton" #footer>
      <div class="flex justify-end">
        <Button
          v-if="showOrganisationPicker"
          variant="secondary"
          size="xs"
          data-cy="org-select-modal-logout-button"
          @click="authStore.logout()"
        >
          <template #left-icon>
            <ArrowRightEndOnRectangleIcon class="h-4 w-4" />
          </template>
          {{ t('general.logout') }}
        </Button>
        <Button v-else data-cy="close-role-event-modal" @click="dismiss">
          {{ t('general.dismiss') }}
        </Button>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import router from '@/router';
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import i18n from '@/plugins/vue-i18n';
import { Avatar, Button, Dialog, IconWrapper, Spinner, Tag } from '@boqnet/ui';
import { useAuthStore } from '@/stores/auth.store';
import { setNewOrg } from '@/common/utils/user.utils';
import { usePusherStore } from '@/stores/pusher.store';
import { getRoleDisplayName } from '@/common/utils/role.utils';
import { ArrowRightEndOnRectangleIcon, ChevronRightIcon } from '@heroicons/vue/24/outline';
import { REFRESHABLE_ROUTE_NAMES } from '@/common/utils/pusher.utils';
import { useRoleEvent } from '@/composables/use-role-event';

const { t } = i18n.global;

const authStore = useAuthStore();
const pusherStore = usePusherStore();

const { roleMessage, showModal } = storeToRefs(pusherStore);

const { details, organisations, isOrgRemoval, showOrganisationPicker, dismiss } = useRoleEvent();

const isOpen = computed({
  get: () => showModal.value,
  set: (open: boolean) => {
    if (!open) void dismiss();
  },
});

const title = computed(() =>
  isOrgRemoval.value ? t('users.roleEvents.orgRemoved.title') : details.value.title,
);

const showDismissButton = computed(
  () => organisations.value.length > 0 && !showOrganisationPicker.value,
);

const showEntityName = computed(() =>
  REFRESHABLE_ROUTE_NAMES.includes(router.currentRoute.value.name as string),
);

const showSpinner = computed(() => organisations.value.length === 0);
</script>
